<template>
  <v-card flat>
    <v-card-title class="text-h5">
      Statistical Calculations
    </v-card-title>

    <v-card-subtitle>
      This module allow the user to calculate the risk of deforestation, the allocated baseline estimated and the historical and current deforestation and forest area inside projects area.
      <br/>The module also provides an assessment table comparing the results obtained with the observed deforestation during the forecast period.
    </v-card-subtitle>

    <v-card-text>
      <v-row>
        <!-- Input form -->
        <v-col sm=4>
          <v-card elevation="4">
            <v-card-title>Projects</v-card-title>
            <v-card-text>
              <v-form v-model="isValid">
                <v-row>
                  <v-col sm="6">
                    <!-- <p class="text-body-1 pt-4">Projects</p> -->
                    <v-select v-model="projects" :items="projectFiles" item-text="NOMBRE" multiple return-object label="Projects" prepend-icon="mdi-file" outlined dense :rules="rules"/>
                  </v-col>
                </v-row>

                <p class="text-body-1 pt-4">Calculation Type</p>
                <v-radio-group v-model="calculationType" row>
                  <v-radio :value="0" label="Allocated deforestation"/>
                  <v-radio :value="1" label="Deforestation"/>
                  <v-radio :value="2" label="Forest"/>
                </v-radio-group>

                <p class="text-body-1 pt-4">Calculation Period</p>
                <v-radio-group v-model="calculationPeriod" row :disabled="calculationType != 0">
                  <v-radio :value="0" label="Period 2"/>
                  <v-radio :value="1" label="Period 1+2"/>
                </v-radio-group>
              </v-form>

              <v-btn width="150" color="primary" :disabled="!isValid" :loading="isInitProcessing" @click="process">Process</v-btn>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Results -->
        <v-col>
          <v-card elevation="8">
            <v-card-title>Results</v-card-title>
            <v-card-text>
              <!-- <v-btn @click= requestChart()>Actualizar grafico</v-btn> -->
              <apexchart v-if="calculationType==0" width="90%" height="600" type="bar" :options="chartOptions" :series="graphicsData.series"></apexchart>
            </v-card-text>

            <v-row>
              <v-card-actions class="px-4">
                <v-col>
                  <v-btn color="primary" outlined @click="downloadFile(csvFile)" :disabled="!csvFile">
                    <v-icon left>mdi-download</v-icon>
                    Download CSV
                  </v-btn>
                </v-col>
                <v-col>
                  <v-btn color="primary" outlined @click="downloadFile(accAssessmentFile)" :disabled="!accAssessmentFile">
                    <v-icon left>mdi-download</v-icon>
                    Download Accuracy Assessment Table
                  </v-btn>
                </v-col>
              </v-card-actions>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'

import api from '../../api'

export default {
  name: 'Module5',

  props: {
    region: Object,
    currentProcess: String
  },

  data: () => ({
    rules: [
      v => !!v || 'Required.',
      v => v.length > 0 || 'Must pick at least one.'
    ],
    isValid: false,

    // Periodic checking
    isInitProcessing: false,
    isProcessing: false,
    intervalCall: null,
    finishedProcessing: false,

    // Input
    projects: [],
    calculationType: 0,
    calculationPeriod: 0,

    // Output
    csvFile: null,
    accAssessmentFile: null,

    // Graphics
    graphicsData: {
      series: [
        {
          name: 'Serie1', data: []
        },
        {
          name: 'Serie2', data: []
        },
        {
          name: 'Serie3', data: []
        }
      ]
    },

    categories: ['Project1']
  }),

  computed: {
    projectFiles () {
      return this.$store.getters['file/filesByRegionAndType'](this.region, 29) // 29 == Project shapefile
    },

    chartOptions () {
      return {
        chart: {
          type: 'bar'
        },
        plotOptions: {
          bar: {
            horizontal: true,
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: true,
          offsetX: -6,
          style: {
            fontSize: '12px',
            colors: ['#fff']
          },
          formatter: (val, opts) => {
            return Math.round(val * 100) / 100
          }
        },
        stroke: {
          show: true,
          width: 1,
          colors: ['#fff']
        },
        tooltip: {
          shared: true,
          intersect: false,
          fixed: {
            enabled: true,
            position: 'topRight'
          }
        },
        xaxis: {
          categories: this.categories
        },
        yaxis: {
          decimalsInFloat: 2
        }
      }
    },
    resultFiles () {
      return [this.csvFile]
    }
  },

  watch: {
    isProcessing (val) {
      this.isInitProcessing = val
      let extrainfo = {
        calculationType: this.calculationType
      }
      if (val && !this.intervalCall) {
        this.intervalCall = setInterval(() => {
          api.process.getResults(this.currentProcess, 5, extrainfo).then(response => {
            if (response.data.error) {
              this.stopProcessing()

              console.error(response.data.error)
              this.$toastr.e(response.data.message)
            } else {
              if (response.data.finished) {
                // CSV Graphics
                if (this.calculationType === 0) {
                  this.updateGraphics(response.data.results.csvGraphics)
                  this.accAssessmentFile = response.data.results.accuracyAssessmentTable
                }

                // CSV Zip
                this.csvFile = response.data.results.csvFile
                // Save results in store
                // TODO: this.setMod4Results(this.resultFiles)

                this.$toastr.s('Module 5 succesfully processed')

                // Ready for results
                this.finishedProcessing = true
                this.$emit('onProcessed')

                this.stopProcessing()
              } else {
                // Not finished
              }
            }
          }).catch(error => {
            this.stopProcessing()

            console.error(error)
            this.$toastr.e('Error processing module 5')
          }).finally(() => {
            // Nothing
          })
        }, 10 * 1000)
      } else {
        clearInterval(this.intervalCall)
        this.intervalCall = null
      }
    }
  },

  methods: {
    ...mapActions('app', [
      'setIsProcessing'
    ]),

    ...mapActions('currentProcess', [
      'setMod5Results' // TODO:
    ]),

    ...mapActions('file', [
      'downloadFile'
    ]),

    stopProcessing () {
      this.isProcessing = false
      clearInterval(this.intervalCall)
      this.intervalCall = null

      this.setIsProcessing(false)
    },

    reset () {
      this.finishedProcessing = false

      // Input
      this.projects = []
      this.calculationType = 0
      this.calculationPeriod = 0

      // Output
      // TODO:
      this.csvFile = null
      this.accAssessmentFile = null
      this.clearGraphics()
    },

    async process () {
      let data = {
        processId: this.currentProcess,
        projects: this.projects,
        calculationType: this.calculationType,
        calculationPeriod: this.calculationPeriod
      }
      // Eliminamos el grafico antiguo y los archivos
      this.clearGraphics()
      this.csvFile = null
      this.accAssessmentFile = null
      try {
        this.setIsProcessing(true)
        this.isInitProcessing = true

        await api.process.module5(data)

        this.isProcessing = true
        this.$toastr.s('Module 5 starts processing...')
      } catch (error) {
        this.isInitProcessing = false

        console.error(error)
        this.$toastr.e('Error initializing module 5')
        this.setIsProcessing(false)
      }
    },

    updateGraphics (gd) {
      this.categories = gd.categories
      this.graphicsData = {
        series: [
          { name: 'Allocated', data: gd.allocatedData },
          { name: 'Assumed Baseline', data: gd.assumedBaselineData },
          { name: gd.deforName, data: gd.deforestationData }
        ]
      }
    },
    requestChart () {
      api.process.testCSV5('23d0d472-a7ee-42e6-9062-02c597e08de5').then(response => {
        // console.log(response.data)
        this.updateGraphics(response.data)
      })
    },
    clearGraphics () {
      this.graphicsData = {
        series: [
          {
            name: 'Serie1', data: []
          },
          {
            name: 'Serie2', data: []
          },
          {
            name: 'Serie3', data: []
          }
        ]
      }
      this.categories = ['Project1']
    }
  }
}
</script>

<style scoped>

</style>
